import { useEffect, useState } from 'react';
import useFiltersState from '../../../../../state/useFiltersState';
import { ICountryDetail } from '../../../../../types/countries';
import ModuleFilters, { TgetFiltersList } from '../../../../../components/ModuleFilters/ModuleFilters';
import { PRODUCT_CONDITION, STOCK_ORDER_STATUS } from '../../../../../types/products';
import { listOrganizations } from '../../../../../services/bussines';
import { Iorganization } from '../../../../../types/bussines';
import { displayErrorNotification } from '../../../../../utils/displayNudosStandardNotifications';
import { LOCATION_PLACE_TYPE } from '../../../../../types/services';
import { REFERENCE_MODEL_TYPE } from 'types/global';
import { TOOL_STATUS_NAME } from '../../../../../types/tools';
import { BordOneToneIcon } from 'components/NudosComponents';
import { LIST_PRODUCTS_TYPE } from 'types/orders';
import { metabaseUrlProductStock } from 'constants/constants';
import './ProductsModuleListingFilters.scss';

const ProductsModuleListingFilters = ({
  listOfCountries,
  listProductsType
}: {
  listOfCountries: ICountryDetail[];
  listProductsType: LIST_PRODUCTS_TYPE;
}) => {
  const { productsModuleListingFilters, setProductsModuleListingFilters } = useFiltersState();
  const [organizationsList, setOrganizationsList] = useState<Iorganization[]>([]);
  const [openedClientFilter, setOpenedClientFilter] = useState<boolean>(false);
  const [openedConditionFilter, setOpenedConditionFilter] = useState<boolean>(false);
  const [openedStatusFilter, setOpenedStatusFilter] = useState<boolean>(false);
  const [openOriginCountryFilter, setOpenOriginCountryFilter] = useState<boolean>(false);
  const [openedPlaceFilter, setOpenedPlaceFilter] = useState<boolean>(false);
  const [openedTypeFilter, setOpenedTypeFilter] = useState<boolean>(false);
  const [openedStockStatusFilter, setOpenedStockStatusFilter] = useState<boolean>(false);

  const isStockBord = listProductsType === LIST_PRODUCTS_TYPE.STOCK_BORD;

  const conditionRawOptions = [
    PRODUCT_CONDITION.NEW,
    PRODUCT_CONDITION.USED_LIKE_NEW,
    PRODUCT_CONDITION.USED_GOOD,
    PRODUCT_CONDITION.USED_FAIR,
    PRODUCT_CONDITION.USED_POOR,
    PRODUCT_CONDITION.USED_VERY_POOR
  ];

  const statusRawOptions = [
    TOOL_STATUS_NAME.AVAILABLE,
    TOOL_STATUS_NAME.BUY_BACK,
    TOOL_STATUS_NAME.SOLD_TO_THIRD_PARTY,
    TOOL_STATUS_NAME.LOST,
    TOOL_STATUS_NAME.DONATED,
    TOOL_STATUS_NAME.DAMAGED,
    TOOL_STATUS_NAME.DESTROYED,
    TOOL_STATUS_NAME.OUTBOUND_SHIPMENT
  ];

  const placesRawOptions = ['Bodega', 'Diagnóstico', 'Garantía', 'Empleado', 'Oficina', 'Otra dirección'];

  const stockStatusRawOptions = [
    STOCK_ORDER_STATUS.TO_ARRIVE,
    STOCK_ORDER_STATUS.STORED,
    STOCK_ORDER_STATUS.RESERVED,
    STOCK_ORDER_STATUS.DISPATCHED
  ];

  const allCountriesRawOptions =
    listOfCountries && listOfCountries.length
      ? listOfCountries.reduce<string[]>((accum, current) => {
          if (current?.name) accum.push(current.name);
          return accum;
        }, [])
      : undefined;

  const organizationsRawOptions = organizationsList.map(organization => organization.name);

  const productsModuleListingFiltersMapping = {
    Client: 'organizationIds',
    Country: 'countryCodes',
    Condition: 'conditions',
    Status: 'toolStatus',
    Place: 'places',
    Type: 'referenceModel',
    StockOrderStatus: 'stockOrderStatus'
  };

  const placesTexts = [
    [LOCATION_PLACE_TYPE.NUDOS, 'Bodega'],
    [LOCATION_PLACE_TYPE.DIAGNOSTIC, 'Diagnóstico'],
    [LOCATION_PLACE_TYPE.WARRANTY, 'Garantía'],
    [LOCATION_PLACE_TYPE.USER, 'Empleado'],
    [LOCATION_PLACE_TYPE.OFFICE, 'Oficina'],
    [LOCATION_PLACE_TYPE.OTHER, 'Otra dirección']
  ];

  const countryNameToCodeMap: Map<string, string> = listOfCountries
    ? new Map(listOfCountries?.map(countryData => [countryData.name, countryData.code]))
    : new Map();

  const organizationNameToIdMap: Map<string, string> = organizationsList
    ? new Map(organizationsList?.map(organization => [organization.name, organization.id]))
    : new Map();

  const placesNameToIdMap: Map<string, string> = placesTexts
    ? new Map(placesTexts?.map(place => [place[1], place[0]]))
    : new Map();

  const typeRawOptions = ['Herramienta Bord', 'Herramienta externa', 'Herramienta Stock propio'];
  const typeNameToCodeMap: Map<string, string> = new Map([
    ['Herramienta Bord', REFERENCE_MODEL_TYPE.INTERNAL],
    ['Herramienta externa', REFERENCE_MODEL_TYPE.EXTERNAL],
    ['Herramienta Stock propio', REFERENCE_MODEL_TYPE.STOCK]
  ]);

  const getOrganizationsList = async () => {
    try {
      const organizationsList = await listOrganizations();
      setOrganizationsList(organizationsList);
    } catch (error) {
      displayErrorNotification();
    }
  };

  const openMetabase = () => window.open(metabaseUrlProductStock, '_blank');

  const getProductsModuleListingFiltersList: TgetFiltersList = (
    formModuleFilters,
    handleClickFilterOption,
    getFilterActiveStatus
  ) => [
    ...(!isStockBord
      ? [
          {
            title: 'Cliente',
            openFunction: setOpenedClientFilter,
            openStatus: openedClientFilter,
            rawSelectOptions: formModuleFilters('Client', organizationsRawOptions, organizationNameToIdMap),
            getModuleFiltersRequestFunction: listOrganizations,
            handleChangeSelectedOption: handleClickFilterOption('organizationIds', 'value'),
            isFilterActive: getFilterActiveStatus('organizationIds'),
            sortAlphabetically: true
          }
        ]
      : []),
    {
      title: 'País',
      openFunction: setOpenOriginCountryFilter,
      openStatus: openOriginCountryFilter,
      rawSelectOptions: formModuleFilters('Country', allCountriesRawOptions, countryNameToCodeMap),
      handleChangeSelectedOption: handleClickFilterOption('countryCodes', 'value'),
      isFilterActive: getFilterActiveStatus('countryCodes'),
      sortAlphabetically: true,
      searchPlaceholder: 'Buscar país',
      showSearch: true
    },
    ...(!isStockBord
      ? [
          {
            title: 'Estado',
            openFunction: setOpenedStatusFilter,
            openStatus: openedStatusFilter,
            rawSelectOptions: formModuleFilters('Status', statusRawOptions),
            handleChangeSelectedOption: handleClickFilterOption('toolStatus', 'value'),
            isFilterActive: getFilterActiveStatus('toolStatus')
          }
        ]
      : []),
    {
      title: 'Condición',
      openFunction: setOpenedConditionFilter,
      openStatus: openedConditionFilter,
      rawSelectOptions: formModuleFilters('Condition', conditionRawOptions),
      handleChangeSelectedOption: handleClickFilterOption('conditions', 'value'),
      isFilterActive: getFilterActiveStatus('conditions')
    },
    ...(!isStockBord
      ? [
          {
            title: 'Ubicación actual',
            openFunction: setOpenedPlaceFilter,
            openStatus: openedPlaceFilter,
            rawSelectOptions: formModuleFilters('Place', placesRawOptions, placesNameToIdMap),
            handleChangeSelectedOption: handleClickFilterOption('places', 'value'),
            isFilterActive: getFilterActiveStatus('places')
          }
        ]
      : []),
    ...(!isStockBord
      ? [
          {
            title: 'Tipo',
            openFunction: setOpenedTypeFilter,
            openStatus: openedTypeFilter,
            rawSelectOptions: formModuleFilters('Type', typeRawOptions, typeNameToCodeMap),
            handleChangeSelectedOption: handleClickFilterOption('referenceModel', 'value'),
            isFilterActive: getFilterActiveStatus('referenceModel'),
            isLargeFilter: true
          }
        ]
      : []),

    ...(isStockBord
      ? [
          {
            title: 'Estado de Stock',
            openFunction: setOpenedStockStatusFilter,
            openStatus: openedStockStatusFilter,
            rawSelectOptions: formModuleFilters('StockOrderStatus', stockStatusRawOptions),
            handleChangeSelectedOption: handleClickFilterOption('stockOrderStatus', 'value'),
            isFilterActive: getFilterActiveStatus('stockOrderStatus')
          }
        ]
      : [])
  ];

  useEffect(() => {
    getOrganizationsList();
  }, []);

  return (
    <div className="productsModuleListingFilters">
      <ModuleFilters
        customGap={24}
        moduleFilters={productsModuleListingFilters}
        getFiltersList={getProductsModuleListingFiltersList}
        countryCondition={true}
        setModuleFilters={setProductsModuleListingFilters}
        moduleFiltersTitlesMapping={productsModuleListingFiltersMapping}
      />

      {isStockBord && (
        <button className="inventoryButton" onClick={openMetabase}>
          <BordOneToneIcon variant="download" customWidth="16px" stroke="#54A1FC" />
          Descargar lista de herramientas
        </button>
      )}
    </div>
  );
};

export default ProductsModuleListingFilters;
